import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  InsertInvitationOutlined,
  PersonPinOutlined,
  PlaylistAddCheck,
  TimerOutlined,
  WifiTethering,
  WorkOutline,
} from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Checkbox, Fade, Grid, Link, PopoverPosition, Typography } from '@mui/material';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { format, isBefore } from 'date-fns';
import { sort } from 'fast-sort';
import {
  Estate,
  EstateTaskInstance,
  EstateTaskInstance_GetAllEstateTaskInstancesWithDeadlineByPortalIdQuery,
  EstateTaskInstanceStatus,
  useEstate_GetEstateByIdLazyQuery,
  useEstateTaskInstance_GetAllEstateTaskInstancesWithDeadlineByPortalIdQuery,
  useEstateTaskInstance_UpdateAssignedUserMutation,
  useEstateTaskInstance_UserUpdateStatusToCompletedMutation,
  useEstateTaskInstance_UserUpdateStatusToUnassignedMutation,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';

import HomeLayout from 'layouts/home-layout';
import AssignedTaskDrawer from 'pages/assigned-tasks-page/components/assigned-task-drawer';
import EstateTaskInstanceCustomDeadlinePopover from 'pages/estate-checklist/components/estate-task-custom/estate-task-instance-custom-deadline-popover';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { canIntegrateWithCalendar } from 'utils/feature-checker';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import { DeadlineTrackerPointPerson } from './components/deadline-tracker-point-person/deadline-tracker-point-person';
import { DeadlineTrackerStatus } from './components/deadline-tracker-status';
import { DeadlineTrackerTaskLabel } from './components/deadline-tracker-task-label/deadline-tracker-task-label';
import { useStyles } from './deadline-tracker-page.styles';

type DeadlineTrackerEstateTaskInstance =
  EstateTaskInstance_GetAllEstateTaskInstancesWithDeadlineByPortalIdQuery['data'][0];

export const DeadlineTrackerPage = () => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [activeEstateWorkItemId, setActiveEstateWorkItemId] = useState<string | null>(null);
  const [activeEstate, setActiveEstate] = useState<Estate | null>(null);
  const [currentEstateTaskInstance, setCurrentEstateTaskInstance] =
    useState<EstateTaskInstance | null>(null);
  const [customDeadlinePopoverAnchorPosition, setCustomDeadlinePopoverAnchorPosition] =
    useState<PopoverPosition | null>(null);

  const {
    data: estateTaskInstanceData,
    loading: isLoadingEstateTaskInstances,
    refetch: refreshEstateTaskInstances,
  } = useEstateTaskInstance_GetAllEstateTaskInstancesWithDeadlineByPortalIdQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        portalId,
        filter: {
          statuses: [EstateTaskInstanceStatus.Assigned, EstateTaskInstanceStatus.Unassigned],
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate task instances by portal ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [getEstate] = useEstate_GetEstateByIdLazyQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleOpenAssignedTaskDrawer = useCallback(
    async (estateTaskInstance: DeadlineTrackerEstateTaskInstance) => {
      const { data: estateData } = await getEstate({
        variables: { id: estateTaskInstance.estateId },
      });

      if (!estateData) {
        return;
      }
      setActiveEstate(estateData?.data as Estate);
      setActiveEstateWorkItemId(estateTaskInstance.estateWorkItemId || null);
    },
    [getEstate],
  );

  const { data: featureSubscriptionTypeData, loading: isLoadingFeatureSubscriptionType } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [markTaskCompleted] = useEstateTaskInstance_UserUpdateStatusToCompletedMutation({
    onCompleted: () => refreshEstateTaskInstances(),
    onError: (e) => {
      showAlert({
        message: 'Error user updating status to completed: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [markTaskUnassigned] = useEstateTaskInstance_UserUpdateStatusToUnassignedMutation({
    onCompleted: () => refreshEstateTaskInstances(),
    onError: (e) => {
      showAlert({
        message: 'Error user updating status to unassigned: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updateAssignedUser] = useEstateTaskInstance_UpdateAssignedUserMutation({
    onCompleted: () => refreshEstateTaskInstances(),
    onError: (e) => {
      showAlert({
        message: 'Error updating estate task instance: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleToggleCheckbox = useCallback(
    (estateTaskInstance: DeadlineTrackerEstateTaskInstance) => {
      if (
        estateTaskInstance.status === EstateTaskInstanceStatus.Assigned ||
        (estateTaskInstance.status === EstateTaskInstanceStatus.Completed &&
          estateTaskInstance.estateWorkItemId)
      ) {
        return;
      }
      if (!window.confirm('Are you sure you want to mark this task as complete?')) {
        return;
      }
      const taskCompleted = estateTaskInstance.status === EstateTaskInstanceStatus.Completed;

      if (taskCompleted) {
        markTaskUnassigned({
          variables: {
            id: estateTaskInstance.id,
          },
        });
      } else {
        markTaskCompleted({
          variables: {
            id: estateTaskInstance.id,
          },
        });
      }
    },
    [markTaskCompleted, markTaskUnassigned],
  );

  const handleUpdateAssignedUser = useCallback(
    (estateTaskInstanceId: string, assignedUserId: string | null) => {
      updateAssignedUser({
        variables: {
          input: { id: estateTaskInstanceId, assignedUserId },
        },
      });
    },
    [updateAssignedUser],
  );

  const handleCloseCustomDeadlinePopover = useCallback(() => {
    setCurrentEstateTaskInstance(null);
  }, []);

  const handleShowCustomDeadlinePopover = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    const anchorEl = event.currentTarget;
    const rect = anchorEl.getBoundingClientRect();
    setCustomDeadlinePopoverAnchorPosition({
      top: rect.top,
      left: rect.left - rect.width - 30,
    });
  };

  const featureSubscriptionType = featureSubscriptionTypeData?.data;
  const hasCalendarFeature = featureSubscriptionType
    ? canIntegrateWithCalendar(featureSubscriptionType)
    : false;

  const estateTaskInstanceColumns = useMemo(() => {
    const columns: GridColDef<DeadlineTrackerEstateTaskInstance>[] = [
      {
        field: 'checkbox',
        headerName: '',
        editable: false,
        width: 25,
        sortable: false,
        hideSortIcons: true,
        headerClassName: 'checkbox-column',
        align: 'center',
        filterable: false,
        renderCell: ({ row }) => (
          <Checkbox
            className={classes.checkbox}
            color="default"
            checked={row.status === EstateTaskInstanceStatus.Completed}
            disableRipple
            disableFocusRipple
            disabled={
              row.status === EstateTaskInstanceStatus.Assigned ||
              (row.status === EstateTaskInstanceStatus.Completed && !!row.estateWorkItemId)
            }
            onClick={() => handleToggleCheckbox(row)}
          />
        ),
      },
      {
        field: 'label',
        headerName: 'Task',
        flex: 6,
        headerClassName: 'estateTasksHeader',
        valueGetter: ({ row }) => (row.label || '') + (row.labelHint || ''),
        renderHeader: () => (
          <Box className={classes.estateTaskHeader}>
            <PlaylistAddCheck />
            <Typography className={classes.estateTaskHeaderName}>Task</Typography>
          </Box>
        ),
        renderCell: ({ row }) => <DeadlineTrackerTaskLabel estateTaskInstance={row} />,
      },
      {
        flex: 2,
        disableColumnMenu: true,
        field: 'Client Matter',
        headerClassName: 'estateTasksHeader',
        valueGetter: ({ row }) => row.estate.estateName,
        renderHeader: () => (
          <Box className={classes.estateTaskHeader}>
            <WorkOutline fontSize="small" />
            <Typography className={classes.estateTaskHeaderName}>Client Matter</Typography>
          </Box>
        ),
        renderCell: ({ row }) => (
          <Link
            href={`/home/${portalId}/estate/${row.estate.id}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`}
          >
            {row.estate.estateName}
          </Link>
        ),
      },
      {
        field: 'Point Person',
        headerName: 'Point Person',
        flex: 2,
        align: 'left',
        headerClassName: 'estateTasksHeader',
        valueGetter: ({ row }) =>
          row.assignedUser?.firstName || '' + row.assignedUser?.lastName || '',
        renderHeader: () => (
          <Box className={classes.estateTaskHeader}>
            <PersonPinOutlined />
            <Typography className={classes.estateTaskHeaderName}>Point Person</Typography>
          </Box>
        ),
        renderCell: ({ row }) => (
          <DeadlineTrackerPointPerson
            assignedUserId={row.assignedUserId}
            portalId={portalId}
            onAssignedUserChange={(selectedUserId) =>
              handleUpdateAssignedUser(row.id, selectedUserId)
            }
          />
        ),
      },
      {
        field: 'Status',
        headerName: 'Status',
        flex: 2,
        headerClassName: 'estateTasksHeader',
        filterable: false,
        renderHeader: () => (
          <Box className={classes.estateTaskHeader}>
            <WifiTethering />
            <Typography className={classes.estateTaskHeaderName}>Status</Typography>
          </Box>
        ),
        renderCell: ({ row }) => (
          <DeadlineTrackerStatus
            estateTaskInstance={row}
            handleOpenAssignedTaskDrawer={() => handleOpenAssignedTaskDrawer(row)}
          />
        ),
      },
      {
        flex: 2,
        disableColumnMenu: true,
        field: 'Deadline',
        headerClassName: 'estateTasksHeader',
        valueGetter: ({ row }) =>
          row.deadlineDate ? format(Date.parse(row.deadlineDate.toString()), 'MM/dd/yyyy') : 'N/A',
        renderHeader: () => (
          <Box className={classes.estateTaskHeader}>
            <TimerOutlined fontSize="small" />
            <Typography className={classes.estateTaskHeaderName}>Deadline</Typography>
          </Box>
        ),
        renderCell: ({ row }) => {
          return (
            <Typography>
              <Grid xs={12} alignItems="center" container>
                <Grid className={classes.deadlineText} xs={6} item>
                  {row.deadlineDate
                    ? format(Date.parse(row.deadlineDate.toString()), 'MM/dd/yyyy')
                    : 'N/A'}
                  <EditIcon
                    className={classes.editDeadlineIcon}
                    fontSize="small"
                    onClick={(evt) => {
                      setCurrentEstateTaskInstance({
                        ...row,
                        customDeadlineDate: row.deadlineDate,
                      } as EstateTaskInstance);
                      handleShowCustomDeadlinePopover(evt);
                    }}
                  />
                </Grid>
              </Grid>
            </Typography>
          );
        },
      },
      {
        flex: 2,
        disableColumnMenu: true,
        field: 'reminder',
        filterable: false,
        headerClassName: 'estateTasksHeader',
        renderHeader: () => (
          <Box className={classes.estateTaskHeader}>
            <InsertInvitationOutlined fontSize="small" />
            <Typography className={classes.estateTaskHeaderName}>Reminder</Typography>
          </Box>
        ),
        renderCell: ({ row }) => {
          if (!hasCalendarFeature) {
            return <Typography>*Upgrade to Add to Calendar</Typography>;
          }

          return (
            <AddToCalendarButton
              name={row.label || undefined}
              startDate={
                row.deadlineDate
                  ? new Date(row.deadlineDate).toISOString().slice(0, 10)
                  : new Date().toISOString().slice(0, 10)
              }
              endDate={
                row.deadlineDate
                  ? new Date(row.deadlineDate).toISOString().slice(0, 10)
                  : new Date().toISOString().slice(0, 10)
              }
              options={['Apple', 'Google', 'MicrosoftTeams', 'Microsoft365', 'Yahoo']}
              buttonStyle="text"
              listStyle="modal"
              size="0|0|0"
              hideBackground={true}
              disabled={!hasCalendarFeature}
            />
          );
        },
      },
    ];
    return columns;
  }, [
    classes,

    handleToggleCheckbox,
    handleUpdateAssignedUser,
    handleOpenAssignedTaskDrawer,
    hasCalendarFeature,
    portalId,
  ]);

  const isLoading =
    isLoadingFeatureSubscriptionType ||
    isLoadingEstateTaskInstances ||
    !featureSubscriptionTypeData?.data ||
    !estateTaskInstanceData?.data;
  const estateTaskInstances = estateTaskInstanceData?.data || [];

  const sortedEstateTaskInstances = sort(estateTaskInstances).asc(
    ({ deadlineDate }) => deadlineDate,
  );
  const now = new Date();
  const pastDeadlineTaskInstances = sortedEstateTaskInstances.filter(({ deadlineDate }) =>
    isBefore(new Date(deadlineDate), now),
  );
  const futureDeadlineTaskInstances = sortedEstateTaskInstances.filter(
    ({ deadlineDate }) => !isBefore(new Date(deadlineDate), now),
  );

  return (
    <HomeLayout>
      <Fade in={true}>
        <Box className={classes.body}>
          <Box>
            <Typography className={classes.header}>Deadline Tracker</Typography>
            <Typography>
              Easily access all your important deadlines and essential information in one convenient
              location.
            </Typography>
          </Box>
          <Box className={classes.dataGridWrapper}>
            <Typography className={classes.deadlineTableTitle}>
              Upcoming Deadlines ({futureDeadlineTaskInstances.length})
            </Typography>
            <DataGrid
              loading={isLoading}
              className={classes.estateTaskDataGrid}
              columns={estateTaskInstanceColumns}
              rows={futureDeadlineTaskInstances}
              disableColumnReorder
              disableRowSelectionOnClick
              disableColumnMenu
              keepNonExistentRowsSelected
              getRowHeight={() => 'auto'}
              autoHeight
              pagination
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              slots={{
                toolbar: () => (
                  <Box>
                    <GridToolbarFilterButton />
                  </Box>
                ),
              }}
            />
          </Box>
          <Box className={classes.dataGridWrapper}>
            <Typography className={classes.deadlineTableTitle}>
              Past Deadlines ({pastDeadlineTaskInstances.length})
            </Typography>
            <DataGrid
              loading={isLoading}
              className={classes.estateTaskDataGrid}
              columns={estateTaskInstanceColumns}
              rows={pastDeadlineTaskInstances}
              disableColumnReorder
              disableRowSelectionOnClick
              disableColumnMenu
              keepNonExistentRowsSelected
              getRowHeight={() => 'auto'}
              autoHeight
              pagination
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              slots={{
                toolbar: () => (
                  <Box>
                    <GridToolbarFilterButton />
                  </Box>
                ),
              }}
            />
          </Box>
          {activeEstateWorkItemId && activeEstate && (
            <AssignedTaskDrawer
              estateWorkItemId={activeEstateWorkItemId}
              estate={activeEstate}
              onClose={() => {
                setActiveEstateWorkItemId(null);
                setActiveEstate(null);
              }}
            />
          )}
          {currentEstateTaskInstance && customDeadlinePopoverAnchorPosition && (
            <EstateTaskInstanceCustomDeadlinePopover
              estateTaskInstance={currentEstateTaskInstance}
              anchorPosition={customDeadlinePopoverAnchorPosition}
              onClose={handleCloseCustomDeadlinePopover}
              replaceEstateTaskInstance={() => refreshEstateTaskInstances()}
            />
          )}
        </Box>
      </Fade>
    </HomeLayout>
  );
};
